import { React, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ListGroup, Button, Card } from 'flowbite-react';
import { HiCloudDownload, HiArrowLeft, HiNewspaper, HiOutlineTrash } from 'react-icons/hi';
import IconComponent from '../../components/Icon';
import PermissionWrapper from '../../hooks/PermissionWrapper';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import api from '../../lib/api';
import { downloadBlobResponse } from '../../lib/downloads';

const SUMMARY_SECTION = 'summary';

const Solution = () => {
  const { solutionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [types, setTypes] = useState([]);
  const [contents, setContents] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreview, setPreview] = useState(false);
  const [activeSection, setActiveSection] = useState(SUMMARY_SECTION);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    return () => {
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);

  const buildSummaryData = (file) => ({
    solution_id: file.solution_id,
    solution_name: file.Solution?.name || '',
    file_id: file.id,
    file_name: file.name,
    file_manager: file.Manager?.name || '',
    file_description: file.description,
    file_path: file.path,
    file_doc_path: file.doc_path,
    file_page_num: file.file_page_num,
    file_version: file.version,
    file_date: file.created_at ? file.created_at.replace('T', ' ').replace('.000Z', '') : '',
    asignados: file.Solution?.Users || [],
  });

  const loadPreview = async (file) => {
    if (pdfFile) {
      URL.revokeObjectURL(pdfFile);
      setPdfFile(null);
    }

    const previewEnabled = import.meta.env.VITE_DOCPREVIEW === 'true';
    setPreview(previewEnabled);
    if (!previewEnabled || !file?.doc_path?.toLowerCase().endsWith('.pdf')) {
      return;
    }

    try {
      const responsePdf = await api.get(`/files/${file.id}/mejora/download`, {
        params: { disposition: 'inline' },
        responseType: 'arraybuffer',
      });
      const url = window.URL.createObjectURL(new Blob([responsePdf.data], { type: 'application/pdf' }));
      setPdfFile(url);
    } catch (previewError) {
      console.error(previewError);
    }
  };

  const handleLoadData = async (fileOrId) => {
    setIsLoading(true);
    setError(null);
    try {
      let file = fileOrId;
      if (!fileOrId?.id) {
        const response = await api.get(`/file/${fileOrId}/data`);
        file = response.data;
      }

      setSelectedFileId(file.id);
      setSelectedFile(buildSummaryData(file));
      await loadPreview(file);
    } catch (loadError) {
      console.error(loadError);
      setError(loadError.response?.data?.message || 'No fue posible cargar el instalador seleccionado.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/app');
  };

  const handleDownloadMejora = async () => {
    if (!selectedFile?.file_id) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/files/${selectedFile.file_id}/mejora/download`, {
        responseType: 'blob',
      });
      downloadBlobResponse(response, selectedFile.file_doc_path || 'documento-mejora');
    } catch (downloadError) {
      alert(downloadError.response?.data?.message || 'El archivo no está disponible');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInstaller = async () => {
    if (!selectedFile?.file_id) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/files/${selectedFile.file_id}/download`, {
        responseType: 'blob',
      });
      downloadBlobResponse(response, selectedFile.file_name || 'instalador');
    } catch (downloadError) {
      alert(downloadError.response?.data?.message || 'No fue posible descargar el instalador.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadContent = async (content) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/contents/${content.id}/download`, {
        responseType: 'blob',
      });
      downloadBlobResponse(response, content.name || 'contenido');
    } catch (downloadError) {
      alert(downloadError.response?.data?.message || 'No fue posible descargar el contenido.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!selectedFileId) {
      return;
    }

    const remove = confirm('Confirma la eliminación del instalador?');
    if (!remove) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.delete(`/file/${selectedFileId}/delete`);
      if (response.data.status) {
        alert(`Instalador eliminado correctamente ${response.data.e || ''}`);
        navigate('/app');
      } else {
        alert('Problema al eliminar el archivo, intenta nuevamente');
      }
    } catch (removeError) {
      alert(removeError.response?.data?.message || 'No fue posible eliminar el instalador.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [filesResponse, typesResponse, contentsResponse] = await Promise.all([
          api.get(`/filesBySolution/${solutionId}`),
          api.get('/content_types'),
          api.get(`/contentBySolution/${solutionId}`),
        ]);

        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [];
        setFiles(filesData);
        setTypes(Array.isArray(typesResponse.data) ? typesResponse.data : []);
        setContents(Array.isArray(contentsResponse.data) ? contentsResponse.data : []);

        const requestedFileId = searchParams.get('instalador');
        const firstFile = requestedFileId
          ? filesData.find((item) => String(item.id) === String(requestedFileId))
          : filesData[0];

        if (firstFile) {
          await handleLoadData(firstFile);
        } else {
          setSelectedFile(null);
        }
      } catch (initialError) {
        console.error(initialError);
        setError(initialError.response?.data?.message || 'No fue posible cargar la solución.');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [solutionId, searchParams]);

  return (
    <>
      {isLoading && <LoadingSpinner message="Procesando..." size="lg" color="blue" />}
      <div className="flex p-2 mt-4">
        <div>
          <h1 className="text-xl mb-2">{selectedFile?.solution_name || 'Solución'}</h1>
          {error && <div className="text-red-500">{error}</div>}
        </div>
        <div>
          <a href="#" onClick={handleBack}>
            <HiArrowLeft className="ml-3 h-6 w-6 text-gray-400" />
          </a>
        </div>
      </div>

      <div className="lg:flex">
        <div className="lg:w-1/2 w-full max-w-sm mt-1.5">
          <div className="flex justify-center">
            <ListGroup className="w-full">
              {files.map((file) => (
                <ListGroup.Item
                  key={file.id}
                  active={selectedFileId === file.id}
                  onClick={() => handleLoadData(file)}
                >
                  {file.name}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>
        <div className="md:mx-2 mt-1.5 w-full">
          <div className="border-gray-200 border p-2 rounded-lg">
            <div className="p-1 mt-2 flex">
              <div className="flex-1">
                <h1 className="text-xl mb-2 text-orange-600">{selectedFile?.file_name || 'Sin instalador seleccionado'}</h1>
              </div>
              <PermissionWrapper requiredPermissions={['installer_remove']}>
                <div className="justify-end">
                  <Button color="gray" onClick={handleRemoveFile} className="button" disabled={!selectedFileId}>
                    <HiOutlineTrash className="w-5 text-blue-500 mr-3" title="Eliminar archivo" />
                    Eliminar instalador
                  </Button>
                </div>
              </PermissionWrapper>
            </div>

            <div className="p-1 overflow-x-auto">
              <Button.Group className="w-full">
                <Button
                  color={activeSection === SUMMARY_SECTION ? 'cyan' : 'gray'}
                  onClick={() => setActiveSection(SUMMARY_SECTION)}
                >
                  <HiNewspaper className="mr-3 h-4 w-4" />
                  Resumen
                </Button>
                {types.map((type) => (
                  <Button
                    key={type.id}
                    color={activeSection === type.id ? 'cyan' : 'gray'}
                    onClick={() => setActiveSection(type.id)}
                  >
                    <IconComponent iconName={type.icon} />
                    {type.name}
                  </Button>
                ))}
              </Button.Group>
            </div>

            <div className="p-1">
              {activeSection === SUMMARY_SECTION && selectedFile && (
                <div>
                  <div className="flex flex-wrap">
                    <div className="m-2 flex-col">
                      <h2 className="text-sm font-medium m-2">Responsable: <i className="text-gray-400">{selectedFile.file_manager}</i></h2>
                    </div>
                    <div className="m-2 flex-col">
                      <h2 className="text-sm font-medium m-2">Liberación: <i className="text-gray-400">{selectedFile.file_date}</i></h2>
                    </div>
                    <div className="m-2 flex-col">
                      <h2 className="text-sm font-medium m-2">Versión: <i className="text-gray-400">{selectedFile.file_version}</i></h2>
                    </div>
                  </div>

                  <div className="flex">
                    <div className="m-2 flex-col">
                      <h2 className="text-sm font-medium m-2">
                        <span className="mb-2">Responsables de solución </span>
                        <table className="mt-2">
                          <tbody>
                            <tr className="border">
                              <td className="p-2">Nombre</td>
                              <td className="p-2">Email</td>
                              <td className="p-2">Rol</td>
                            </tr>
                            {selectedFile.asignados.map((asignado) => (
                              <tr key={asignado.id} className="border">
                                <td className="p-2"><i className="text-gray-400">{asignado.name}</i></td>
                                <td className="p-2"><i className="text-gray-400">{asignado.email}</i></td>
                                <td className="p-2"><i className="text-gray-400">{asignado.Roles?.[0]?.name || ''}</i></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </h2>
                    </div>
                  </div>

                  {selectedFile.file_description && (
                    <div className="flex border border-gray-300 mb-2">
                      <div className="text-gray-400 p-2">{selectedFile.file_description}</div>
                    </div>
                  )}

                  {isPreview && pdfFile && (
                    <div className="contentPdfViewer">
                      <div style={{ height: '300px' }}>
                        <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js">
                          <Viewer fileUrl={pdfFile} initialPage={selectedFile.file_page_num || 0} />
                        </Worker>
                      </div>
                    </div>
                  )}

                  <div className="flex mt-4">
                    <div className="m-2">
                      <Button onClick={handleDownloadMejora}>
                        Descargar Doc. mejora
                        <HiCloudDownload className="mr-3 h-6 w-6" />
                      </Button>
                    </div>
                    <div className="m-2">
                      <Button className="bg-gray-800" onClick={handleDownloadInstaller}>
                        Descargar solución
                        <HiCloudDownload className="mr-3 h-6 w-6" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {types.map((type) => {
                if (activeSection !== type.id) {
                  return null;
                }

                const typeContents = contents.filter((content) => content.content_type_id === type.id);

                return (
                  <div key={type.id} className="lg:grid lg:grid-cols-4">
                    {typeContents.map((content) => {
                      if (content.Content_type.type === 'doc' || content.Content_type.type === 'mejora') {
                        return (
                          <Card key={content.id} className="max-w-sm m-2">
                            <h5 className="font-bold tracking-tight text-gray-900 dark:text-white">{content.name}</h5>
                            <p className="text-sm text-gray-700 dark:text-gray-400">{content.description}</p>
                            <Button onClick={() => handleDownloadContent(content)}>
                              Descargar
                            </Button>
                          </Card>
                        );
                      }

                      if (content.Content_type.type === 'video') {
                        const status = content.processing_status || (content.path ? 'ready' : 'failed');
                        const isProcessing = status === 'queued' || status === 'processing';
                        const isFailed = status === 'failed';

                        return (
                          <Card key={content.id} className="max-w-sm m-2">
                            {status === 'ready' && content.path && (
                              <iframe
                                className="rounded-lg"
                                width="100%"
                                height="200px"
                                src={`https://www.youtube.com/embed/${content.path}`}
                                title={content.name}
                                frameBorder="0"
                                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            )}
                            {isProcessing && (
                              <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-600">
                                Video en procesamiento interno. Actualiza en unos minutos.
                              </div>
                            )}
                            {isFailed && (
                              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                El video tuvo un problema al procesarse. Reintenta la carga.
                              </div>
                            )}
                            <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{content.name}</h5>
                            <p className="font-normal text-gray-700 dark:text-gray-400">{content.description}</p>
                          </Card>
                        );
                      }

                      return null;
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Solution;
